import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import Select from "react-select";
import ReactDatePicker from "react-datepicker";
import { Link } from "react-router-dom";

import { TOURNAMENT_LOGO_URL } from "../../lib/globals";
import { defaultDateFormat, getTournamentCosts } from "../../lib/utils";
import { IField, IMasterTournament, ITournament } from "../../types/api";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { globalActions, selectFields } from "../../features/global/globalSlice";

interface Filters {
    ageLevel: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    level: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    city: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    startDate?: Date;
    endDate?: Date;
}

const TournamentOfferings = ({
    tournamentOfferings,
    tournaments,
    display,
}: {
    tournamentOfferings: IMasterTournament[];
    tournaments: ITournament[];
    display?: "list" | "grid";
}): React.JSX.Element => {
    const displayType = display
        ? display === "list"
            ? "tournamentList"
            : "tournamentGrid"
        : "tournamentList";
    const dispatch = useAppDispatch();
    const loadedFields = useAppSelector(selectFields);
    const [fields, setFields] = useState<IField[]>(loadedFields);

    useEffect(() => {
        if (loadedFields.length === 0) {
            dispatch(globalActions.getFields())
                .unwrap()
                .then(result => {
                    setFields(result);
                    setFilters(prev => ({
                        ...prev,
                        city: {
                            ...prev.city,
                            options: Array.from(
                                new Set(
                                    result
                                        .filter(f =>
                                            tournamentOfferings.some(
                                                t => t.field.id === f.id,
                                            ),
                                        )
                                        .flatMap(f => `${f.city}, ${f.state}`),
                                ),
                            ).sort(),
                        },
                    }));
                });
        }
    }, [dispatch, loadedFields]);

    const [filters, setFilters] = useState<Filters>({
        ageLevel: {
            options: Array.from(
                new Set(
                    tournaments
                        .filter(t =>
                            tournamentOfferings.some(to => to.id === t.parent),
                        )
                        .flatMap(t => t.ageLevel),
                ),
            ).sort((a, b) => {
                return parseInt(a) - parseInt(b);
            }),
            filter: undefined,
        },
        level: {
            options: Array.from(
                new Set(tournamentOfferings.flatMap(t => t.designation)),
            ).sort(),
            filter: undefined,
        },
        city: {
            options: Array.from(
                new Set(
                    fields
                        .filter(f =>
                            tournamentOfferings.some(t => t.field.id === f.id),
                        )
                        .flatMap(f => `${f.city}, ${f.state}`),
                ),
            ).sort(),
            filter: undefined,
        },
        startDate: undefined,
        endDate: undefined,
    });

    const { earliestStart, latestStart, earliestEnd, latestEnd } =
        tournamentOfferings.reduce(
            (acc, mt) => {
                if (
                    !acc.earliestStart ||
                    new Date(mt.startDate) < new Date(acc.earliestStart)
                ) {
                    acc.earliestStart = new Date(mt.startDate);
                }

                if (
                    !acc.latestStart ||
                    new Date(mt.startDate) > new Date(acc.latestStart)
                ) {
                    acc.latestStart = new Date(mt.startDate);
                }

                if (
                    !acc.latestEnd ||
                    new Date(mt.endDate) > new Date(acc.latestEnd)
                ) {
                    acc.latestEnd = new Date(mt.endDate);
                }

                if (
                    !acc.earliestEnd ||
                    new Date(mt.endDate) < new Date(acc.earliestEnd)
                ) {
                    acc.earliestEnd = new Date(mt.endDate);
                }

                return acc;
            },
            {
                earliestStart: undefined,
                latestStart: undefined,
                earliestEnd: undefined,
                latestEnd: undefined,
            } as {
                earliestStart?: Date;
                latestStart?: Date;
                earliestEnd?: Date;
                latestEnd?: Date;
            },
        );

    return (
        <>
            <Row className="tournamentFilters mt-5 mb-2">
                <Col xs={12}>
                    <h4>Search Tournaments</h4>
                </Col>
                <Col xs={12} md={6} xl={4} className="my-2">
                    <h5>City:</h5>
                    <Select
                        isSearchable={true}
                        isClearable={true}
                        isMulti={true}
                        options={filters.city.options.map((l, idx) => {
                            return {
                                value: idx,
                                label: l,
                            };
                        })}
                        onChange={l => {
                            setFilters(prev => ({
                                ...prev,
                                city: {
                                    ...prev.city,
                                    filter:
                                        l.length === 0
                                            ? undefined
                                            : l?.map(x => x.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col xs={6} xl={2} className="my-2">
                    <h5>Age Level:</h5>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.ageLevel.options.map((a, idx) => {
                            return {
                                value: idx,
                                label: a,
                            };
                        })}
                        onChange={a => {
                            setFilters(prev => ({
                                ...prev,
                                ageLevel: {
                                    ...prev.ageLevel,
                                    filter:
                                        a.length === 0
                                            ? undefined
                                            : a?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col xs={6} sm={6} md={4} xl={2} className="my-2">
                    <h5>Competition Level:</h5>
                    <Select
                        isSearchable={true}
                        isClearable={true}
                        isMulti={true}
                        options={filters.level.options.map((l, idx) => {
                            return {
                                value: idx,
                                label: l,
                            };
                        })}
                        onChange={l => {
                            setFilters(prev => ({
                                ...prev,
                                level: {
                                    ...prev.level,
                                    filter:
                                        l.length === 0
                                            ? undefined
                                            : l?.map(x => x.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col xs={6} md={4} xl={2} className="my-2">
                    <h5>Earliest Start Date:</h5>
                    <ReactDatePicker
                        selected={
                            filters.startDate
                                ? new Date(filters.startDate)
                                : undefined
                        }
                        onChange={d =>
                            setFilters(prev => ({
                                ...prev,
                                startDate: d || undefined,
                            }))
                        }
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        minDate={earliestStart}
                        maxDate={latestStart}
                        dateFormat="MMMM d, yyyy"
                        dropdownMode="select"
                        className="form-control"
                    />
                </Col>
                <Col xs={6} md={4} xl={2} className="my-2">
                    <h5>Latest End Date:</h5>
                    <ReactDatePicker
                        selected={
                            filters.endDate
                                ? new Date(filters.endDate)
                                : undefined
                        }
                        onChange={d =>
                            setFilters(prev => ({
                                ...prev,
                                endDate: d || undefined,
                            }))
                        }
                        isClearable
                        minDate={earliestEnd}
                        maxDate={latestEnd}
                        showMonthDropdown
                        showYearDropdown
                        dateFormat="MMMM d, yyyy"
                        dropdownMode="select"
                        className="form-control"
                    />
                </Col>
            </Row>

            <Row className="mt-5 mb-2">
                <Col>
                    <h4>Tournament Offerings</h4>
                    <section id={displayType} className="mt-3">
                        {tournamentOfferings.length ? (
                            tournamentOfferings
                                .filter(t =>
                                    filters.ageLevel.filter
                                        ? tournaments.some(
                                              ct =>
                                                  ct.parent === t.id &&
                                                  filters.ageLevel.filter?.includes(
                                                      ct.ageLevel,
                                                  ),
                                          )
                                        : true,
                                )
                                .filter(t =>
                                    filters.city.filter
                                        ? fields.some(
                                              f =>
                                                  f.id === t.field.id &&
                                                  filters.city.filter?.includes(
                                                      `${f.city}, ${f.state}`,
                                                  ),
                                          )
                                        : true,
                                )
                                .filter(t =>
                                    filters.level.filter
                                        ? filters.level.filter?.includes(
                                              t.designation,
                                          ) || t.designation === "Open"
                                        : true,
                                )
                                .filter(t => {
                                    return filters.startDate
                                        ? new Date(
                                              defaultDateFormat.format(
                                                  new Date(t.startDate),
                                              ),
                                          ) >=
                                              new Date(
                                                  defaultDateFormat.format(
                                                      filters.startDate,
                                                  ),
                                              )
                                        : true;
                                })
                                .filter(t => {
                                    return filters.endDate
                                        ? new Date(
                                              defaultDateFormat.format(
                                                  new Date(t.endDate),
                                              ),
                                          ) <=
                                              new Date(
                                                  defaultDateFormat.format(
                                                      filters.endDate,
                                                  ),
                                              )
                                        : true;
                                })
                                .map(t => {
                                    const tournamentCosts = getTournamentCosts(
                                        tournaments.filter(
                                            ct => ct.parent === t.id,
                                        ),
                                    );
                                    return (
                                        <div className="tournament" key={t.id}>
                                            <h4>
                                                <Link
                                                    to={`/tournaments/${t.id}/overview`}
                                                    state={{
                                                        tournament: {
                                                            id: t.id,
                                                            name: t.name,
                                                            startDate:
                                                                t.startDate,
                                                            endDate: t.endDate,
                                                        },
                                                    }}
                                                >
                                                    {t.name}
                                                </Link>
                                            </h4>
                                            <div className="tournamentDetails">
                                                <div className="tournamentLogo">
                                                    {t.logo && (
                                                        <Link
                                                            to={`/tournaments/${t.id}/overview`}
                                                            state={{
                                                                tournament: {
                                                                    id: t.id,
                                                                    name: t.name,
                                                                    startDate:
                                                                        t.startDate,
                                                                    endDate:
                                                                        t.endDate,
                                                                },
                                                            }}
                                                        >
                                                            <img
                                                                src={`${TOURNAMENT_LOGO_URL}${t.logo}`}
                                                                alt={`${t.name} logo`}
                                                                className="tournamentLogo"
                                                            />
                                                        </Link>
                                                    )}
                                                </div>

                                                <div className="tournamentInfo">
                                                    <ul>
                                                        <li>
                                                            {defaultDateFormat.format(
                                                                new Date(
                                                                    t.startDate,
                                                                ),
                                                            )}{" "}
                                                            -{" "}
                                                            {defaultDateFormat.format(
                                                                new Date(
                                                                    t.endDate,
                                                                ),
                                                            )}
                                                        </li>
                                                        <li className="ageLevels">
                                                            Age Groups:{" "}
                                                            {tournaments
                                                                .filter(
                                                                    ct =>
                                                                        ct.parent ===
                                                                        t.id,
                                                                )
                                                                .map(t => (
                                                                    <span
                                                                        key={
                                                                            t.id
                                                                        }
                                                                    >
                                                                        {t.maxTeams &&
                                                                        (t.teams
                                                                            ? t
                                                                                  .teams
                                                                                  .length >=
                                                                              parseInt(
                                                                                  t.maxTeams?.toString(),
                                                                              )
                                                                            : false) ? (
                                                                            <s>
                                                                                {
                                                                                    t.ageLevel
                                                                                }
                                                                            </s>
                                                                        ) : (
                                                                            t.ageLevel
                                                                        )}
                                                                    </span>
                                                                ))}
                                                        </li>
                                                        <li>
                                                            Cost:{" "}
                                                            {tournamentCosts}
                                                        </li>
                                                        <li>
                                                            Competition Level:{" "}
                                                            {t.designation}
                                                        </li>
                                                        <li>
                                                            <Link
                                                                to={`/fields/${t.field.id}`}
                                                            >
                                                                {t.field.name}
                                                            </Link>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                        ) : (
                            <h3>
                                No current tournament offerings. Please check
                                back later
                            </h3>
                        )}
                    </section>
                </Col>
            </Row>
        </>
    );
};

export default TournamentOfferings;
